// Declination — Core types
// Decision provenance captured at the moment of choice.
// Status anxiety shows up as declination: the angle between the composite "pulls" (mostly external status cues)
// and the private "true north" the freelancer actually cares about for the work.

export type CueCategory =
  | 'visibility'          // "this will photograph / case-study well"
  | 'client_status'       // "this client is prestigious / will open doors"
  | 'peer_comparison'     // "people I respect did something like this"
  | 'positioning'         // "this moves my rates / positioning forward"
  | 'craft_truth'         // "this actually serves the real outcome / user / material"
  | 'energy'              // "this will sustain or drain me over the next weeks"
  | 'precedent'           // "I did something similar and it worked (or didn't)"
  | 'risk_status'         // "this feels socially risky / might look amateur / too much"

export const CUE_LABELS: Record<CueCategory, string> = {
  visibility: 'Visibility / Portfolio signal',
  client_status: 'Client status / impressiveness',
  peer_comparison: 'Peer comparison',
  positioning: 'Positioning / future rates',
  craft_truth: 'Craft truth / actual outcome',
  energy: 'My energy & sustainability',
  precedent: 'Precedent that worked',
  risk_status: 'Social / status risk',
}

export const CUE_IS_STATUS: Record<CueCategory, boolean> = {
  visibility: true,
  client_status: true,
  peer_comparison: true,
  positioning: true,
  craft_truth: false,
  energy: false,
  precedent: false,
  risk_status: true,
}

export interface Pull {
  id: string
  cueCategory: CueCategory
  cueDetail: string          // the specific language the person used ("the client said their CMO loves bold work")
  strength: number           // 0-100
  angle: number              // 0-360, visual direction of the magnetic pull
}

export interface Decision {
  id: string
  userId: string
  createdAt: string
  decisionText: string       // "Choosing between the illustrated direction vs the clean typographic one"
  privateNorth: string       // "What would actually be best for the client's audience and my energy on revisions"
  trustPrior: number         // 1-5 how much they trust their own read on this *kind* of decision right now
  hesitationNote?: string
  pulls: Pull[]
  declination: number        // computed 0-180 degrees (absolute angular difference between composite bearing and true north)
  releasedAt?: string
  releaseNote?: string       // private conviction at moment of sending/publishing
  // For calibration
  expectedVelocity?: number  // 1-5 how fast/strong they expect external signals to arrive
}

export interface Signal {
  id: string
  decisionId: string
  loggedAt: string
  kind: 'client' | 'public' | 'internal' | 'commercial' | 'silence' | 'other'
  note: string
  valence: -1 | 0 | 1         // negative, neutral, positive reception
  magnitude: number           // 1-5 rough strength of this signal
}

export interface HesitationCapture {
  id: string
  capturedAt: string
  decisionId?: string
  text: string                // "I almost went with the safe version because it would look more 'professional' on my own site"
  cueTrigger?: CueCategory
}

export interface CurveSnapshot {
  id: string
  decisionId: string
  frozenAt: string
  r2PngKey?: string
  r2JsonKey?: string
  declinationAtFreeze: number
  summary: string
  // The data at time of freeze (for self-contained export)
  decision: Decision
  signals: Signal[]
}

export interface CalibrationInsight {
  totalDecisions: number
  highStatusPullDecisions: number
  avgDeclination: number
  factualStatements: string[]   // pure descriptive, never prescriptive
}

// For the queue
export interface EnrichMessage {
  type: 'decision_saved' | 'signal_logged' | 'snapshot_frozen'
  decisionId?: string
  userId: string
  payload?: any
}
